import React, { useEffect, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd-cjs';
import { getEmptyImage } from 'react-dnd-html5-backend-cjs';
import { DraggableType } from '../../types/dnd';
import { DndSortableElementId, DndSortableElementRenderOptions } from './types';
import type { Identifier } from 'dnd-core';

interface DndItem {
  type: string;
  id: DndSortableElementId;
}

export interface Props {
  dndType: DraggableType;
  dndAcceptType?: DraggableType | DraggableType[];
  id: DndSortableElementId;
  position: number;
  isSortable?: boolean;
  customPreview?: boolean;
  onDragBegin: (id: DndSortableElementId, position: number) => void;
  onDragEnd: () => void;
  onHover: (position: number) => void;
  onDrop: (position: number) => void;
  children: (options: DndSortableElementRenderOptions) => any;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

const DndSortableElement: React.FC<Props> = ({
  dndType,
  dndAcceptType,
  children,
  id,
  position,
  onDrop,
  onHover,
  onDragBegin,
  onDragEnd,
  customPreview,
  isSortable = true,
}) => {
  const ref = useRef<any>(null);

  const [{ handlerId, isOver }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null; isOver: boolean }
  >({
    accept: dndAcceptType || dndType,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
        isOver: monitor.isOver(),
      };
    },

    drop: (item: DndItem) => {
      if (!ref.current) {
        return;
      }
      onDrop(position);
    },

    hover(item: DndItem) {
      if (!ref.current) {
        return;
      }
      if (id !== item.id) {
        onHover(position);
      }
    },

    canDrop(item, monitor) {
      return isSortable;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    item: {
      type: dndType,
      id,
    },
    begin: () => {
      onDragBegin(id, position);
    },
    end: () => {
      onDragEnd();
    },
    canDrag: isSortable,
    collect: (monitor: any) => {
      return { isDragging: monitor.isDragging() };
    },
  });

  drag(drop(ref));

  useEffect(() => {
    if (customPreview) {
      preview(getEmptyImage(), { captureDraggingState: true });
    }
  }, [preview, customPreview]);

  return children({ ref, 'data-handler-id': handlerId, isDragging, isOver });
};

DndSortableElement.displayName = 'DndSortableElement';

export default DndSortableElement;
